/* Centred popups, in one place.

   The site had grown three of them — the size table, the SGS report and the
   two dialogs the nav links open — each with its own entrance, its own way of
   locking the page and its own idea of what Escape should do. They are one
   component now: the section writes the content, this writes the behaviour.

   The contract is data attributes, so a section never has to ship a script:

     <dialog class="nagi-modal" data-nagi-modal id="x">
       <div class="nagi-modal__inner" tabindex="-1"> … </div>
     </dialog>
     <button data-nagi-modal-open="x">        opens it, from anywhere on the page
     <button data-nagi-modal-close>           closes it, from inside it
     data-nagi-modal-link="/pages/x"          any link whose href contains this
                                              opens the dialog instead
     data-nagi-modal-reopen                   after the form inside posts, the
       + an element marked data-nagi-modal-ok is what proves it came back a
       thank-you, and the dialog reopens on it

   Every listener is delegated from the document, so a dialog rendered later —
   by an app, by a section the editor just added — needs no registration. */
(function () {
  'use strict';

  var LOCK = 'nagi-modal-lock';
  var OUT_MS = 230;
  var SENT_KEY = 'nagi-modal-sent-';

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var store = {
    get: function (k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
    del: function (k) { try { sessionStorage.removeItem(k); } catch (e) {} }
  };

  function isOpen(dlg) {
    return !!dlg && dlg.hasAttribute('open');
  }

  /* The page behind a modal must not scroll, but the class goes on only while
     at least one is open — two dialogs handing over must not leave the page
     frozen because the one that left cleaned up after the one that arrived. */
  function syncLock() {
    var any = document.querySelector('dialog.nagi-modal[open]');
    document.documentElement.classList.toggle(LOCK, !!any);
  }

  function hardClose(dlg) {
    if (typeof dlg.close === 'function') dlg.close();
    else dlg.removeAttribute('open');
    syncLock();
  }

  function open(dlg) {
    if (!dlg || isOpen(dlg)) return;
    /* One at a time. A button inside one dialog may point at the link another
       answers, and two stacked modals is nobody's answer to "how do I reach
       you". The one leaving goes at once — an exit animation playing under an
       entrance reads as a glitch, not as a transition. */
    Array.prototype.forEach.call(document.querySelectorAll('dialog.nagi-modal[open]'), function (other) {
      if (other !== dlg) { other.classList.remove('is-closing'); hardClose(other); }
    });
    dlg.classList.remove('is-closing');
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
    syncLock();
    /* Focus the panel, not the first control. showModal's own choice is the
       close button, which opens the dialog wearing a focus ring around an X;
       focusing the first field would throw a phone keyboard up before anyone
       asked for one. */
    var panel = dlg.querySelector('.nagi-modal__inner');
    if (panel && panel.focus) panel.focus({ preventScroll: true });
  }

  /* A timer rather than animationend: reduced motion strips the animation, and
     a dialog waiting on an event that never fires would simply refuse to
     close. */
  function close(dlg) {
    if (!dlg || !isOpen(dlg)) return;
    if (reduced() || typeof dlg.close !== 'function') { hardClose(dlg); return; }
    if (dlg.classList.contains('is-closing')) return;
    dlg.classList.add('is-closing');
    window.setTimeout(function () {
      dlg.classList.remove('is-closing');
      hardClose(dlg);
    }, OUT_MS);
  }

  function byId(id) {
    if (!id) return null;
    var el = document.getElementById(id.charAt(0) === '#' ? id.slice(1) : id);
    return el && el.classList.contains('nagi-modal') ? el : null;
  }

  /* Which dialog, if any, has claimed this href. The link keeps its real
     destination, so a middle-click, a copied link and a visitor whose
     JavaScript never ran all still get the page behind it. */
  function dialogForHref(href) {
    if (!href) return null;
    var hit = null;
    Array.prototype.forEach.call(document.querySelectorAll('dialog.nagi-modal[data-nagi-modal-link]'), function (d) {
      var m = d.getAttribute('data-nagi-modal-link') || '';
      if (m && href.indexOf(m) !== -1) hit = d;
    });
    return hit;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var closer = t.closest('[data-nagi-modal-close]');
    if (closer) {
      var owner = closer.closest('dialog.nagi-modal');
      if (owner) { e.preventDefault(); close(owner); return; }
    }

    var opener = t.closest('[data-nagi-modal-open]');
    if (opener) {
      var target = byId(opener.getAttribute('data-nagi-modal-open'));
      if (target) { e.preventDefault(); open(target); return; }
    }

    /* The backdrop is the dialog element itself; a click landing on it rather
       than on the panel inside is a click outside. */
    if (t.tagName === 'DIALOG' && t.classList.contains('nagi-modal')) { close(t); return; }

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    var a = t.closest('a[href]');
    if (!a || a.target === '_blank') return;
    var claimed = dialogForHref(a.getAttribute('href'));
    if (claimed) { e.preventDefault(); open(claimed); }
  });

  /* Escape routes through the same exit as every other close. */
  document.addEventListener('cancel', function (e) {
    var dlg = e.target;
    if (!dlg || !dlg.classList || !dlg.classList.contains('nagi-modal')) return;
    e.preventDefault();
    close(dlg);
  }, true);

  document.addEventListener('close', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('nagi-modal')) syncLock();
  }, true);

  /* Shopify answers a posted contact form by reloading the page it came from,
     which loses the dialog the form was in. A note left at submit time is what
     tells us this dialog was the sender — the posted flag alone is true for
     every contact form on the page, and the contacts page has one of its own. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.closest) return;
    var dlg = form.closest('dialog.nagi-modal[data-nagi-modal-reopen]');
    if (dlg && dlg.id) store.set(SENT_KEY + dlg.id, '1');
  }, true);

  Array.prototype.forEach.call(document.querySelectorAll('dialog.nagi-modal[data-nagi-modal-reopen]'), function (dlg) {
    if (!dlg.id || !dlg.querySelector('[data-nagi-modal-ok]')) return;
    if (!store.get(SENT_KEY + dlg.id)) return;
    store.del(SENT_KEY + dlg.id);
    open(dlg);
  });

  /* The footer's dialogs have no page of their own — their links are bare
     anchors like #kontaktai. Arriving with one in the address (a shared link,
     or a redirect from the old /pages/ URLs) opens that dialog. */
  if (location.hash.length > 1) {
    var fromHash = dialogForHref(location.hash);
    if (fromHash) open(fromHash);
  }

  window.NagiModal = {
    open: function (ref) { open(typeof ref === 'string' ? byId(ref) : ref); },
    close: function (ref) { close(typeof ref === 'string' ? byId(ref) : ref); }
  };
})();
